// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { Box, Typography, useTheme } from "@outschool/backpack";
import { faPlus, faTrash } from "@outschool/icons";
import { BASE_LOCALE, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { FormField, IconButton } from "@outschool/ui-legacy-component-library";
import ldCountBy from "lodash/countBy";
import ldGroupBy from "lodash/groupBy";
import ldMaxBy from "lodash/maxBy";
import React from "react";
import { v4 } from "uuid";

import TimeSelectInput from "./TimeSelectInput";
import { AvailableTime } from "./types";

function splitTimeString(timeStr: string): { hour: number; minute: number } {
  const obj = dayjs(timeStr, "HH:mm");
  return {
    hour: obj.hours(),
    minute: obj.minutes()
  };
}

function makeTimeStrings(availableTime: AvailableTime): {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
} {
  return {
    dayOfWeek: dayjs().locale(BASE_LOCALE).day(availableTime.day).format("ddd"),
    startTime: dayjs()
      .hour(availableTime.startHour)
      .minute(availableTime.startMinute)
      .format("HH:mm"),
    endTime: dayjs()
      .hour(availableTime.endHour)
      .minute(availableTime.endMinute)
      .format("HH:mm")
  };
}

export default function AvailableTimes({
  availableTimes,
  onDelete,
  onUpdate
}: {
  availableTimes: AvailableTime[];
  onDelete(deleted: AvailableTime): void;
  onUpdate(updated: AvailableTime): void;
}) {
  const { t } = useTranslation(
    "client\\components\\AvailableTimesInput\\AvailableTimesRows"
  );

  const DAYS_OF_WEEK = [
    { label: t("Monday"), value: 1 },
    { label: t("Tuesday"), value: 2 },
    { label: t("Wednesday"), value: 3 },
    { label: t("Thursday"), value: 4 },
    { label: t("Friday"), value: 5 },
    { label: t("Saturday"), value: 6 },
    { label: t("Sunday"), value: 0 }
  ];
  const defaultAddHours = React.useMemo(() => {
    const startEndStrs = availableTimes.map(
      at => `${at.startHour}:${at.startMinute}-${at.endHour}:${at.endMinute}`
    );
    const startEndStrCountTuples = Object.entries(ldCountBy(startEndStrs));
    const mostCommonStartEndStr = ldMaxBy(startEndStrCountTuples, x => x[1]);
    if (!!mostCommonStartEndStr) {
      const [startTimeStr, endTimeStr] = mostCommonStartEndStr[0].split("-");
      const [startHour, startMinute] = startTimeStr.split(":");
      const [endHour, endMinute] = endTimeStr.split(":");
      return {
        startHour: Number.parseInt(startHour),
        startMinute: Number.parseInt(startMinute),
        endHour: Number.parseInt(endHour),
        endMinute: Number.parseInt(endMinute)
      };
    }
    return undefined;
  }, [availableTimes]);

  const groupedByDay = ldGroupBy(availableTimes, ({ day }) => day);

  return (
    <Box>
      {DAYS_OF_WEEK.map(({ label, value }) => (
        <AvailableTimesForDayOfWeek
          key={label}
          dayOfWeek={label}
          availableTimes={groupedByDay[value]}
          onDelete={onDelete}
          onUpdate={onUpdate}
          defaultAddHours={defaultAddHours}
          daysOfWeek={DAYS_OF_WEEK}
        />
      ))}
    </Box>
  );
}

function AvailableTimeInputs({
  availableTime,
  onUpdate
}: {
  availableTime: AvailableTime;
  onUpdate(updated: AvailableTime): void;
}) {
  const { startTime, endTime } = makeTimeStrings(availableTime);

  const handleStartTimeChange = React.useCallback(
    (timeStr: string) => {
      const { hour, minute } = splitTimeString(timeStr);
      onUpdate({
        ...availableTime,
        startHour: hour,
        startMinute: minute
      });
    },
    [onUpdate, availableTime]
  );

  const handleEndTimeChange = React.useCallback(
    (timeStr: string) => {
      const { hour, minute } = splitTimeString(timeStr);
      onUpdate({ ...availableTime, endHour: hour, endMinute: minute });
    },
    [onUpdate, availableTime]
  );

  return (
    <FormField
      sx={{
        borderRadius: "4px",
        transition: "background-color 500ms ease-in-out"
      }}
    >
      <Box
        flex
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%"
        }}
      >
        <Box flex sx={{ alignItems: "center" }}>
          <TimeSelectInput value={startTime} onChange={handleStartTimeChange} />
          <Box
            sx={{ marginLeft: ".8rem", marginRight: ".8rem", flexShrink: 0 }}
          >
            <Typography>-</Typography>
          </Box>
          <TimeSelectInput value={endTime} onChange={handleEndTimeChange} />
        </Box>
      </Box>
    </FormField>
  );
}

type AvailableTimesForDayOfWeekProps = {
  dayOfWeek: string;
  availableTimes: AvailableTime[];
  onDelete(deleted: AvailableTime): void;
  onUpdate(updated: AvailableTime): void;
  defaultAddHours:
    | {
        startHour: any;
        startMinute: any;
        endHour: any;
        endMinute: any;
      }
    | undefined;
  daysOfWeek: Array<{ label: string; value: number }>;
};

function AvailableTimesForDayOfWeek({
  dayOfWeek,
  availableTimes,
  onDelete,
  onUpdate,
  defaultAddHours,
  daysOfWeek
}: AvailableTimesForDayOfWeekProps) {
  const handleAdd = React.useCallback(
    (availableTime?: AvailableTime) => {
      const day = daysOfWeek.find(dow => dow.label === dayOfWeek)!.value;
      function getStartEndHours() {
        if (!availableTime && !!defaultAddHours) {
          return defaultAddHours;
        }
        const startHour = availableTime?.endHour ?? 8;
        const startMinute = availableTime?.endMinute ?? 0;
        const endHour = Math.min(startHour + 1, 23);
        const endMinute = startHour === endHour ? 45 : startMinute;
        return {
          startHour,
          startMinute,
          endHour,
          endMinute
        };
      }
      onUpdate({
        uid: v4(),
        day,
        ...getStartEndHours()
      });
    },
    [onUpdate, dayOfWeek, defaultAddHours, daysOfWeek]
  );

  if (!availableTimes?.length) {
    return (
      <AvailabilityRow
        isFirstRow={true}
        dayOfWeek={dayOfWeek}
        onAdd={() => handleAdd()}
      >
        <Typography>unavailable</Typography>
      </AvailabilityRow>
    );
  }

  return (
    <>
      {availableTimes.map((availableTime, idx) => (
        <AvailabilityRow
          key={`availableTimeRow-${availableTime?.uid}`}
          isFirstRow={idx === 0}
          dayOfWeek={dayOfWeek}
          onDelete={() => onDelete(availableTime)}
          onAdd={
            idx === availableTimes.length - 1 &&
            !isLastAvailabilityTimeSlotOfDay(availableTime)
              ? () => handleAdd(availableTimes[availableTimes.length - 1])
              : undefined
          }
        >
          <AvailableTimeInputs
            availableTime={availableTime}
            onUpdate={onUpdate}
          />
        </AvailabilityRow>
      ))}
    </>
  );
}

function isLastAvailabilityTimeSlotOfDay(availabilityTime: AvailableTime) {
  return availabilityTime.endHour === 23 && availabilityTime.endMinute >= 45;
}

interface AvailabilityRowProps {
  isFirstRow: boolean;
  dayOfWeek: string;
  onDelete?: (availableTime: AvailableTime) => void;
  onAdd?: () => void;
  children: React.ReactNode;
}

const AvailabilityRow = ({
  isFirstRow,
  dayOfWeek,
  onDelete,
  onAdd,
  children
}: AvailabilityRowProps) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: "1rem",
        [theme.breakpoints.down("md")]: {
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "0.25em"
        }
      }}
    >
      <Box sx={{ width: "152px" }}>
        {isFirstRow && (
          <Typography variant={"subtitle1"}>{dayOfWeek}</Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexGrow: 1,
          [theme.breakpoints.down("md")]: {
            width: "100%"
          }
        }}
      >
        <Box
          sx={{
            minHeight: "40px",
            display: "flex",
            alignItems: "center",
            width: "256px"
          }}
        >
          {children}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexShrink: 0,
            gap: "1em",
            marginLeft: "1em"
          }}
        >
          {onDelete && (
            <IconButton
              icon={faTrash}
              iconProps={{ size: "medium" }}
              onClick={onDelete}
              sx={{
                width: "24px",
                height: "24px",
                color: [theme.palette.grey[600]]
              }}
            />
          )}
          {onAdd ? (
            <IconButton
              icon={faPlus}
              iconProps={{ size: "medium" }}
              onClick={onAdd}
              sx={{
                width: "24px",
                height: "24px",
                color: [theme.palette.grey[600]]
              }}
            />
          ) : (
            <Box sx={{ width: "24px" }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};
